import { getAccountId } from '@mc/user-info'
import trackEvent from '../../core/interactors/trackEvent'
import { TRACK_PROPS } from './trackMediaConstants'

export const TRACK_PRODUCT_TYPES = {
  DEFAULT: 'MasterClass',
  MASTER_CAMP: 'MasterCamp',
}

export const TRACK_CONTENT_TYPES = {
  COURSE_CHAPTER: 'Course Chapter',
  COURSE_PREVIEW: 'Course Preview',
  COURSE_SAMPLE: 'Course Sample',
  COURSE_TRAILER: 'Course Trailer',
  CDP_HERO: 'CDP Hero Auto Play',

  CAMP_MARKETING: 'Camp Marketing',
  CAMP_TRAILER: 'Camp Trailer',
  INSTRUCTOR_INTERACTION: 'Instructor Interaction',

  PACKAGE_CHAPTER: 'Package Chapter',

  SERIES_EPISODE: 'Series',
  EPISODE_GEM: 'Episode Gem',

  GEM_PREVIEW: 'Gem Preview',

  SHORTFORM: 'Shortform',
  QUICKLIST: 'Quicklist',
  MARKETING: 'Marketing',

  TASK: 'Task',
  POST_PW_MARKETING: 'Post PW Marketing',

  ONBOARDING: 'Onboarding',
  EMBED: 'Video Embed',

  FLW: 'Frontline Worker',

  CERTIFICATE_TRAILER: 'Certificate Trailer',
  CERTIFICATE_CURRICULUM_ACTIVITY: 'Certificate Curriculum Activity',
  CERTIFICATE_PREVIEW: 'Certificate Preview',
  CERTIFICATE_HERO_AUTO_PLAY: 'Certificate Hero Auto Play',
  // CLP hero loop (MCCERTS-2279). Distinct from the hub hero value above so
  // the two auto-play surfaces are separable in Media Analytics.
  CERTIFICATE_CLP_HERO_AUTO_PLAY: 'Certificate CLP Hero Auto Play',
  CERTIFICATE_SUBSCRIPTION_HERO_AUTO_PLAY:
    'Certificate Subscription Hero Auto Play',

  // Coach (coach-electron) namespaced content types. Distinct from the
  // core-product COURSE_CHAPTER/SERIES_EPISODE values above so Coach playback
  // is separable in Media Analytics. See DAIS-5987 (BE) / DAIS-5988 (FE).
  COACH_COURSE_CHAPTER: 'Coach Course Chapter',
  COACH_SERIES: 'Coach Series',

  // MCX in-platform recordings of past events and AI Labs (JAR-3396). Kept
  // apart from each other, and from INSTRUCTOR_INTERACTION which carries live
  // lessons, so all three are separable in Media Analytics.
  EVENT_RECORDING: 'Event Recording',
  AI_LAB_RECORDING: 'AI Lab Recording',
} as const

type TrackContentKeys = keyof typeof TRACK_CONTENT_TYPES
export type TrackContentType = (typeof TRACK_CONTENT_TYPES)[TrackContentKeys]

export const TRACK_COURSE_TRAILER_TITLE = 'Course Trailer'
export const TRACK_COURSE_PREVIEW_TITLE = 'Course Preview'

export const TRACK_PLAYBACK_EVENTS = {
  STARTED: 'Media Playback Started',
  PAUSED: 'Media Playback Paused',
  INTERRUPTED: 'Media Playback Interrupted',
  SEEK_COMPLETED: 'Media Playback Seek Completed',
  RESUMED: 'Media Playback Resumed',
  COMPLETED: 'Media Playback Completed',
  SKIPPED: 'Media Playback Skipped',
  SETTINGS_TOGGLED: 'Media Playback Settings Toggled',
} as const

export const TRACK_CONTENT_EVENTS = {
  STARTED: 'Media Content Started',
  PLAYING: 'Media Content Playing',
  COMPLETED: 'Media Content Completed',
} as const

export const PLAYER_NAME_TYPES = {
  WEB: 'Web Player',
  CERTS: 'Certs Web Player',
}

const validateSessionIds = (IDs: Record<string, unknown>) => {
  const { PLAYER_SESSION_ID, MEDIA_SESSION_ID } = TRACK_PROPS

  if (!IDs[PLAYER_SESSION_ID]) {
    throw new Error(`Missing ${PLAYER_SESSION_ID}`)
  }
  if (!IDs[MEDIA_SESSION_ID]) {
    throw new Error(`Missing ${MEDIA_SESSION_ID}`)
  }
}

const trackMedia = (
  eventName: string,
  sessionIDs = {},
  metadata: Record<string, string | unknown> = {},
) => {
  validateSessionIds(sessionIDs)

  const { USER_ID } = TRACK_PROPS
  // Honor a user_id supplied anywhere in the merged props (e.g. the tracking
  // context flowing into derivedProps); fall back to getAccountId() only when
  // none was provided. Keep `||` so an empty-string user_id still falls back.
  const props: Record<string, unknown> = { ...sessionIDs, ...metadata }
  trackEvent(eventName, {
    ...props,
    [USER_ID]: props[USER_ID] || getAccountId(),
  })
}

export default trackMedia
